/**
 * BrokerLinkSection — Tradier / Alpaca / IBKR credentials (encrypted server-side).
 * Lives on Profile. Available on every account tier.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";

const BROKERS = [
  { id: "tradier", name: "Tradier", needsSecret: false, accountHint: "VA######## (sandbox) or live account number" },
  { id: "alpaca", name: "Alpaca", needsSecret: true, accountHint: "Optional" },
  { id: "ibkr", name: "Interactive Brokers", needsSecret: false, accountHint: "Linked through TWS — no key required here" },
];

export default function BrokerLinkSection() {
  const [loaded, setLoaded] = useState(false);
  const [connections, setConnections] = useState([]);
  const [supported, setSupported] = useState([]);
  const [activeSource, setActiveSource] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState({});
  const [error, setError] = useState(null);

  const loadAll = async () => {
    try {
      const [me, active] = await Promise.all([
        api.get("/broker/me"),
        api.get("/broker/active-source"),
      ]);
      setConnections(me.data.connections || []);
      setSupported(me.data.supported || []);
      setActiveSource(active.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load broker connections");
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const updateForm = (broker, field, value) =>
    setForm(prev => ({ ...prev, [broker]: { ...(prev[broker] || {}), [field]: value } }));

  const link = async (broker) => {
    const body = form[broker] || {};
    if (!body.api_key) { setError("API key is required"); return; }
    setBusy(prev => ({ ...prev, [broker]: true }));
    setError(null);
    try {
      await api.post("/broker/link", {
        broker,
        api_key: body.api_key,
        api_secret: body.api_secret,
        account_id: body.account_id,
        env: body.env || "paper",
      });
      setForm(prev => ({ ...prev, [broker]: {} }));
      await loadAll();
    } catch (e) {
      setError(e?.response?.data?.detail || "Link failed");
    } finally {
      setBusy(prev => ({ ...prev, [broker]: false }));
    }
  };

  const unlink = async (broker) => {
    setBusy(prev => ({ ...prev, [broker]: true }));
    try {
      await api.delete(`/broker/${broker}`);
      await loadAll();
    } finally {
      setBusy(prev => ({ ...prev, [broker]: false }));
    }
  };

  const test = async (broker) => {
    setBusy(prev => ({ ...prev, [`${broker}_test`]: true }));
    try {
      const { data } = await api.get(`/broker/test/${broker}`);
      alert(`${broker} ping: ${data.connected ? "OK" : "FAILED"} — ${data.message}`);
    } catch (e) {
      alert(`${broker} ping failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(prev => ({ ...prev, [`${broker}_test`]: false }));
      loadAll();
    }
  };

  if (!loaded) return null;

  const connByBroker = Object.fromEntries(connections.map(c => [c.broker, c]));

  return (
    <section className="border p-4 rounded-sm space-y-3" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }} data-testid="broker-link-section">
      <div className="flex items-baseline justify-between">
        <div className="text-[10px] tracking-widest" style={{ color: "var(--hoip-text-tertiary)" }}>BROKER CONNECTIONS</div>
        {activeSource && (
          <div className="text-[10px] flex items-center gap-1" style={{ color: "var(--hoip-text-secondary)" }} data-testid="active-source-badge">
            DATA SOURCE: <span className="px-1.5 py-0.5 font-bold tracking-widest" style={{ background: "var(--hoip-hover)", color: "var(--hoip-text-primary)" }}>15-MIN DELAYED</span>
          </div>
        )}
      </div>

      {error && (
        <div className="text-xs px-2 py-1" style={{ background: "var(--hoip-loss)", color: "#000" }}>{error}</div>
      )}

      <div className="grid grid-cols-1 gap-3">
        {BROKERS.filter(b => supported.includes(b.id)).map(b => {
          const conn = connByBroker[b.id];
          const body = form[b.id] || {};
          return (
            <div key={b.id} className="border p-3 rounded-sm" style={{ borderColor: "var(--hoip-divider)" }} data-testid={`broker-card-${b.id}`}>
              <div className="flex items-center justify-between">
                <div className="text-sm font-bold">{b.name}</div>
                {conn && !conn.needs_relink ? (
                  <span className="text-[9px] tracking-widest px-1.5 py-0.5" style={{ background: "var(--hoip-profit)", color: "#000" }}>LINKED · {conn.env?.toUpperCase()}</span>
                ) : conn?.needs_relink ? (
                  <span className="text-[9px] tracking-widest px-1.5 py-0.5" style={{ background: "var(--hoip-loss)", color: "#000" }}>RE-LINK REQUIRED</span>
                ) : (
                  <span className="text-[9px] tracking-widest px-1.5 py-0.5" style={{ background: "var(--hoip-hover)", color: "var(--hoip-text-tertiary)" }}>NOT LINKED</span>
                )}
              </div>

              {conn && !conn.needs_relink ? (
                <div className="mt-2 flex items-center gap-2 text-xs" style={{ color: "var(--hoip-text-secondary)" }}>
                  <span>Account: <span className="font-mono-data">{conn.account_id || "—"}</span></span>
                  {conn.api_key_last4 && (
                    <span>Key: <span className="font-mono-data">••••{conn.api_key_last4}</span></span>
                  )}
                  <span>Linked: {(conn.linked_at || "").slice(0, 10)}</span>
                  {conn.last_test && (
                    <span style={{ color: conn.last_test.ok ? "var(--hoip-profit)" : "var(--hoip-loss)" }}>
                      • last test {conn.last_test.ok ? "OK" : "FAILED"}
                    </span>
                  )}
                  <div className="ml-auto flex gap-2">
                    <button onClick={() => test(b.id)} disabled={busy[`${b.id}_test`]} className="text-[10px] px-2 py-0.5 border" style={{ borderColor: "var(--hoip-border)" }} data-testid={`broker-test-${b.id}`}>{busy[`${b.id}_test`] ? "TESTING…" : "TEST"}</button>
                    <button onClick={() => unlink(b.id)} disabled={busy[b.id]} className="text-[10px] px-2 py-0.5" style={{ background: "var(--hoip-loss)", color: "#000" }} data-testid={`broker-unlink-${b.id}`}>UNLINK</button>
                  </div>
                </div>
              ) : (
                <div className="mt-2 grid grid-cols-1 md:grid-cols-4 gap-2 text-xs">
                  {conn?.needs_relink && (
                    <div className="col-span-full flex items-center justify-between gap-2">
                      <p className="text-xs" style={{ color: "var(--hoip-loss)" }}>
                        Stored credentials cannot be decrypted (encryption key rotated). Re-enter your {b.name} API key to reconnect.
                      </p>
                      <button onClick={() => unlink(b.id)} disabled={busy[b.id]} className="text-[10px] px-2 py-0.5 shrink-0" style={{ background: "var(--hoip-loss)", color: "#000" }} data-testid={`broker-unlink-${b.id}`}>UNLINK</button>
                    </div>
                  )}
                  <input
                    type="password" placeholder={`${b.name} API key`}
                    value={body.api_key || ""}
                    onChange={e => updateForm(b.id, "api_key", e.target.value)}
                    className="px-2 py-1 border bg-transparent col-span-2"
                    style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-primary)" }}
                    data-testid={`broker-api-key-${b.id}`}
                  />
                  {b.needsSecret && (
                    <input
                      type="password" placeholder="API secret"
                      value={body.api_secret || ""}
                      onChange={e => updateForm(b.id, "api_secret", e.target.value)}
                      className="px-2 py-1 border bg-transparent"
                      style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-primary)" }}
                      data-testid={`broker-api-secret-${b.id}`}
                    />
                  )}
                  <input
                    type="text" placeholder={b.accountHint}
                    value={body.account_id || ""}
                    onChange={e => updateForm(b.id, "account_id", e.target.value)}
                    className="px-2 py-1 border bg-transparent"
                    style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-primary)" }}
                    data-testid={`broker-account-${b.id}`}
                  />
                  <select
                    value={body.env || "paper"}
                    onChange={e => updateForm(b.id, "env", e.target.value)}
                    className="px-2 py-1 border bg-transparent"
                    style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-primary)" }}
                    data-testid={`broker-env-${b.id}`}
                  >
                    <option value="paper">paper</option>
                    <option value="live">live</option>
                  </select>
                  <button
                    onClick={() => link(b.id)} disabled={busy[b.id]}
                    className="px-3 py-1 text-[11px] font-bold tracking-widest"
                    style={{ background: "var(--hoip-text-primary)", color: "var(--hoip-bg)" }}
                    data-testid={`broker-link-${b.id}`}>
                    {busy[b.id] ? "LINKING…" : "LINK"}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>
        Credentials are encrypted at rest with a dedicated server key (not your session secret). MarkovLab never places trades — order tickets only open your broker's UI for self-directed review.
      </p>
    </section>
  );
}
